import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User, Building, Shield, CreditCard, Bell,
  ArrowUpRight, Check, Loader2, Eye, EyeOff, Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  // Profile edit state
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [company, setCompany] = useState(user?.company || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileChanged, setProfileChanged] = useState(false);
  
  // Change password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Track profile changes
  const handleFullNameChange = (e) => {
    setFullName(e.target.value);
    setProfileChanged(e.target.value !== user?.full_name || company !== (user?.company || ''));
  };
  
  const handleCompanyChange = (e) => {
    setCompany(e.target.value);
    setProfileChanged(fullName !== user?.full_name || e.target.value !== (user?.company || ''));
  };

  const handleSaveProfile = async () => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      toast.error('Full name must be at least 2 characters');
      return;
    }
    
    setSavingProfile(true);
    try {
      await axios.put(
        `${API_URL}/auth/profile`,
        { full_name: fullName, company: company || null },
        { withCredentials: true }
      );
      toast.success('Profile updated successfully');
      setProfileChanged(false);
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getPlanDisplay = () => {
    const plan = user?.plan || 'none';
    const status = user?.subscription_status || 'pending';
    
    const planNames = {
      none: 'No Plan',
      basic: 'Basic',
      starter: 'Starter',
      growth: 'Growth',
      agency: 'Agency'
    };
    
    const planColors = {
      none: 'bg-slate-100 text-slate-600',
      basic: 'bg-blue-100 text-blue-700',
      starter: 'bg-indigo-100 text-indigo-700',
      growth: 'bg-purple-100 text-purple-700',
      agency: 'bg-emerald-100 text-emerald-700'
    };
    
    return {
      name: planNames[plan] || plan,
      color: planColors[plan] || planColors.none,
      status: status === 'active' ? 'Active' : 
              status === 'expired' ? 'Expired' : 'Pending'
    };
  };

  const planInfo = getPlanDisplay();
  const hasNoPlan = !user?.plan || user?.plan === 'none';

  return (
    <DashboardLayout>
      <Header 
        title="Settings" 
        subtitle="Manage your account settings"
      />
      
      <div className="p-8 max-w-3xl" data-testid="settings-content">
        {/* Profile Section */}
        <div className="card-dashboard mb-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-slate-400" />
            Profile Information
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={fullName}
                onChange={handleFullNameChange}
                placeholder="Enter your full name"
                className="mt-1"
                data-testid="name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                defaultValue={user?.email || ''}
                className="mt-1 bg-slate-50"
                disabled
                data-testid="email-input"
              />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
            </div>
            <div>
              <Label>Company</Label>
              <Input
                value={company}
                onChange={handleCompanyChange}
                placeholder="Enter your company name (optional)"
                className="mt-1"
                data-testid="company-input"
              />
            </div>
          </div>

          {profileChanged && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <Button 
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="btn-primary"
                data-testid="save-profile-btn"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Subscription Section */}
        <div className="card-dashboard mb-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-400" />
            Subscription
          </h3>

          <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {planInfo.name} Plan
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {user?.leads_used || 0} / {user?.leads_limit || 0} leads used this month
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${planInfo.color}`}>
                {planInfo.status}
              </span>
            </div>
          </div>

          {/* Upgrade Options */}
          <div className="space-y-3">
            {hasNoPlan ? (
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <p className="text-sm font-medium text-emerald-800 mb-2">
                  🚀 Pick a plan to unlock full access
                </p>
                <ul className="text-sm text-emerald-700 space-y-1 mb-3">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> More leads per month
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> AI Email Writer & Call Scripts
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> CRM & CSV Export
                  </li>
                </ul>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => navigate('/pricing')}
                    className="btn-primary"
                    data-testid="upgrade-plan-btn"
                  >
                    View Plans
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/pricing')}
                  data-testid="change-plan-btn"
                >
                  Change Plan
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Notifications Section */}
        <div className="card-dashboard mb-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-slate-400" />
            Notifications
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-700">Email Notifications</p>
                <p className="text-xs text-slate-500">Get notified about follow-ups</p>
              </div>
              <span className="badge-neutral">Coming Soon</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-slate-700">Daily Digest</p>
                <p className="text-xs text-slate-500">Summary of your lead activity</p>
              </div>
              <span className="badge-neutral">Coming Soon</span>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="card-dashboard">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-400" />
            Security
          </h3>

          {!showPasswordForm ? (
            <Button 
              variant="outline" 
              onClick={() => setShowPasswordForm(true)}
              data-testid="change-password-btn"
            >
              Change Password
            </Button>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label>Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  type="submit" 
                  disabled={changingPassword}
                  className="btn-primary"
                  data-testid="submit-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
                <Button 
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  data-testid="cancel-password-btn"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
